import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  InputBase,
  Paper,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import { isInspectionDetailsCategory } from "../../../Helpers/LineRatingUtils";
import { canRelocateCommentBetween } from "../../../Helpers/HelperFunctions";

const FONT_FAMILY = '"Source Sans Pro", "Open Sans", sans-serif';

const getDisplayName = (item) => item?.prompt || item?.name || "";

const templateHeaderSx = {
  fontWeight: 700,
  fontSize: "11px",
  letterSpacing: "0.5px",
  color: "#666666",
  textTransform: "uppercase",
  mt: 1,
  mb: 1,
  display: "block",
  fontFamily: FONT_FAMILY,
};

const matchesSearch = (category, term) =>
  getDisplayName(category).toLowerCase().includes(term) ||
  category.lines.some((line) => getDisplayName(line).toLowerCase().includes(term));

// is_inspection_details value of the Texas "Additional Information" category (1 is Inspection Details)
const ADDITIONAL_INFORMATION_CATEGORY_KIND = 2;

const isCommentDestinationCategory = (category) =>
  category?.is_deleted !== 1 &&
  !isInspectionDetailsCategory(category) &&
  category?.is_inspection_details !== ADDITIONAL_INFORMATION_CATEGORY_KIND;

// Which templates can receive the comment is decided by canRelocateCommentBetween (same kind, or Texas <->
// narrative). Soft-deleted templates/categories/lines, categories without lines, and the Inspection Details and
// Additional Information categories are never destinations.
const buildDestinationTemplates = (templates, sourceTemplate) =>
  templates
    .filter((template) => template?.is_deleted !== 1 && canRelocateCommentBetween(sourceTemplate, template))
    .map((template) => ({
      template,
      categories: (template.categories || [])
        .filter(isCommentDestinationCategory)
        .map((category) => ({
          ...category,
          lines: (category.lines || []).filter((line) => line?.is_deleted !== 1),
        }))
        .filter((category) => category.lines.length > 0),
    }));

const MoveCopyCommentModal = ({
  open,
  handleClose,
  mode = "move", // "move" | "copy"
  currentCategoryId,
  currentCategoryUuid,
  currentLineUuid,
  templateId,
  onConfirm,
}) => {
  const storeData = useSelector((state) => state.CommonReducer);
  const inspectionData = storeData?.inspectionData?.data;

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedDestinations, setSelectedDestinations] = useState([]); // [{ templateId, categoryId, categoryUuid, categoryName, lineId, lineUuid, lineName }]
  const destinationListRef = useRef(null);
  const currentLineRowRef = useRef(null);

  const templates = inspectionData?.inspectiontemplates;
  const destinationTemplates = useMemo(() => {
    const templateList = templates ?? [];
    const sourceTemplate = templateList.find((template) => template.template_id === templateId) ?? templateList[0];
    return buildDestinationTemplates(templateList, sourceTemplate);
  }, [templates, templateId]);

  const isCurrentCategory = (template, category) =>
    template.template_id === templateId &&
    (category.id === currentCategoryId || category.uuid === currentCategoryUuid);

  // Reset on every open; the current category's default expansion is derived at render time
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedDestinations([]);
      setExpandedCategories({});
    }
  }, [open]);

  // Auto expand categories when searching
  useEffect(() => {
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      const searchExpanded = {};
      destinationTemplates.forEach(({ categories }) => {
        categories.forEach((cat) => {
          if (matchesSearch(cat, term)) {
            searchExpanded[cat.uuid || cat.id] = true;
          }
        });
      });
      setExpandedCategories(searchExpanded);
    }
  }, [searchTerm, destinationTemplates]);

  const toggleAccordion = (catKey, isExpanded) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catKey]: !isExpanded,
    }));
  };

  // Templates created from the same master share category/line uuids, so a destination is only identified by
  // its uuids together with the template
  const isSameDestination = (destination, templateIdToMatch, categoryUuid, lineUuid) =>
    destination.templateId === templateIdToMatch &&
    destination.categoryUuid === categoryUuid &&
    destination.lineUuid === lineUuid;

  const isLineSelected = (template, category, line) =>
    selectedDestinations.some((d) => isSameDestination(d, template.template_id, category.uuid || category.id, line.uuid));

  const isCommentCurrentLine = (template, category, line) =>
    isCurrentCategory(template, category) && line.uuid === currentLineUuid;

  const handleSelectLine = (template, category, line) => {
    if (isCommentCurrentLine(template, category, line)) return; // Inactive current line

    const destObj = {
      templateId: template.template_id,
      categoryId: category.id,
      categoryUuid: category.uuid || category.id,
      categoryName: category.prompt || category.name,
      lineId: line.id,
      lineUuid: line.uuid,
      lineName: line.prompt || line.name,
    };

    if (mode === "copy") {
      if (isLineSelected(template, category, line)) {
        setSelectedDestinations((prev) =>
          prev.filter(
            (d) => !isSameDestination(d, destObj.templateId, destObj.categoryUuid, destObj.lineUuid)
          )
        );
      } else {
        setSelectedDestinations((prev) => [...prev, destObj]);
      }
    } else {
      if (isLineSelected(template, category, line)) {
        setSelectedDestinations([]);
      } else {
        setSelectedDestinations([destObj]);
      }
    }
  };

  const handleConfirm = () => {
    if (selectedDestinations.length === 0) return;
    onConfirm(mode === "move" ? selectedDestinations[0] : selectedDestinations);
    handleClose();
  };

  // Filter categories and lines based on search; templates with no match are hidden
  const searchTermLower = searchTerm.trim().toLowerCase();
  const filteredTemplates = destinationTemplates
    .map(({ template, categories }) => ({
      template,
      categories: searchTermLower ? categories.filter((cat) => matchesSearch(cat, searchTermLower)) : categories,
    }))
    .filter(({ categories }) => categories.length > 0);

  const isButtonEnabled = selectedDestinations.length > 0;

  // Open the list where the comment currently lives: once the dialog has finished opening (its category is
  // expanded by default), scroll only the list container so the current line sits in the middle of it.
  const scrollToCurrentLine = () => {
    const list = destinationListRef.current;
    const currentLineRow = currentLineRowRef.current;
    if (!list || !currentLineRow) return;
    const listRect = list.getBoundingClientRect();
    const rowRect = currentLineRow.getBoundingClientRect();
    list.scrollBy({ top: rowRect.top - listRect.top - (list.clientHeight - rowRect.height) / 2, behavior: "smooth" });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{ onEntered: scrollToCurrentLine }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "6px",
          padding: "16px 20px 20px 20px",
          boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
          fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        aria-label="close move modal"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "#777777",
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        <CloseIcon sx={{ fontSize: "20px" }} />
      </IconButton>

      {/* Header Title & Subtitle */}
      <Box sx={{ textAlign: "center", mt: 1, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#252525",
            fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
          }}
        >
          {mode === "copy" ? "Copy Comment" : "Move Comment"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            color: "#666666",
            mt: 0.5,
            fontSize: "14px",
            fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
          }}
        >
          Choose a destination.
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0, overflowY: "visible" }}>
        {/* Search Input Box */}
        <Paper
          elevation={0}
          sx={{
            p: "6px 12px",
            display: "flex",
            alignItems: "center",
            border: "1px solid #D8D8D8",
            borderRadius: "6px",
            mb: 2,
            backgroundColor: "#FFFFFF",
          }}
        >
          <SearchIcon sx={{ color: "#777777", mr: 1, fontSize: "20px" }} />
          <InputBase
            placeholder="Search category or line"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: 1,
              fontSize: "14px",
              color: "#333333",
              fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
            }}
            inputProps={{ "aria-label": "search category or line" }}
          />
        </Paper>

        {/* Categories Tree Accordion Container */}
        <Box
          ref={destinationListRef}
          sx={{
            maxHeight: "320px",
            overflowY: "auto",
            border: "1px solid #E0E0E0",
            borderRadius: "6px",
            p: 1,
            backgroundColor: "#FFFFFF",
          }}
        >
          {filteredTemplates.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: "#888888",
                textAlign: "center",
                py: 3,
                fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
              }}
            >
              No categories or lines found.
            </Typography>
          ) : (
            filteredTemplates.flatMap(({ template, categories: templateCategories }) => [
              <Typography key={`template_header_${template.template_id}`} variant="caption" sx={templateHeaderSx}>
                {template.name || "RESIDENTIAL INSPECTION"}
              </Typography>,
              ...templateCategories.map((category) => {
              const catKey = category.uuid || category.id;
              const isCurrentCat = isCurrentCategory(template, category);
              // The current category starts expanded until the user toggles it
              const isExpanded = expandedCategories[catKey] ?? isCurrentCat;

              // Filter lines inside category if search term exists
              const categoryNameMatches = getDisplayName(category).toLowerCase().includes(searchTermLower);
              const categoryLines = searchTermLower && !categoryNameMatches
                ? category.lines.filter((line) => getDisplayName(line).toLowerCase().includes(searchTermLower))
                : category.lines;

              // Line level inside Category
              return (
                <Accordion
                  key={catKey}
                  expanded={isExpanded}
                  onChange={() => toggleAccordion(catKey, isExpanded)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid #EAEAEA",
                    borderRadius: "6px !important",
                    mb: 1,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#666666" }} />}
                    sx={{
                      px: 1.5,
                      minHeight: "40px",
                      "& .MuiAccordionSummary-content": {
                        margin: "6px 0",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#333333",
                        fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
                      }}
                    >
                      {category.prompt || category.name}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                    {categoryLines.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#999999",
                          px: 1,
                          display: "block",
                          fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
                        }}
                      >
                        No lines in this category
                      </Typography>
                    ) : (
                      categoryLines.map((line) => {
                        const isCurrentLine = isCommentCurrentLine(template, category, line);
                        const isSelected = isLineSelected(template, category, line);

                        return (
                          <Box
                            key={line.uuid || line.id}
                            ref={isCurrentLine ? currentLineRowRef : undefined}
                            onClick={() => handleSelectLine(template, category, line)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              py: 0.8,
                              px: 1,
                              borderRadius: "4px",
                              cursor: isCurrentLine ? "not-allowed" : "pointer",
                              backgroundColor: isSelected
                                ? "#F0F7FF"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: isCurrentLine
                                  ? "transparent"
                                  : "#F7F9FC",
                              },
                            }}
                          >
                            {/* Line Checkbox Icon */}
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                border: isSelected
                                  ? "none"
                                  : isCurrentLine
                                  ? "1px solid #D0D0D0"
                                  : "1.5px solid #777777",
                                backgroundColor: isSelected
                                  ? "#0F75BC"
                                  : isCurrentLine
                                  ? "#EAEAEA"
                                  : "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#FFFFFF",
                                flexShrink: 0,
                              }}
                            >
                              {isCurrentLine ? (
                                <RemoveIcon
                                  sx={{ fontSize: 14, color: "#888888" }}
                                />
                              ) : isSelected ? (
                                <CheckIcon sx={{ fontSize: 14 }} />
                              ) : null}
                            </Box>

                            {/* Line Name */}
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: isSelected ? 600 : 400,
                                color: isCurrentLine ? "#999999" : "#333333",
                                fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
                              }}
                            >
                              {line.prompt || line.name}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </AccordionDetails>
                </Accordion>
              );
              }),
            ])
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            fullWidth
            disabled={!isButtonEnabled}
            onClick={handleConfirm}
            sx={{
              py: 1.2,
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              fontFamily: '"Source Sans Pro", "Open Sans", sans-serif',
              backgroundColor: isButtonEnabled ? "#0F75BC !important" : "#D8D8D8 !important",
              color: isButtonEnabled ? "#FFFFFF !important" : "#888888 !important",
              "&:hover": {
                backgroundColor: isButtonEnabled ? "#0B5E98 !important" : "#D8D8D8 !important",
              },
            }}
          >
            {mode === "copy" ? (selectedDestinations.length > 1 ? `Copy (${selectedDestinations.length})` : "Copy") : "Move"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MoveCopyCommentModal;
